import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Stack,
  CircularProgress,
  TextField,
  IconButton,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import Services from 'src/services/Services';
import api from 'src/services/BaseService';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

if (pdfMake.vfs === undefined && pdfFonts && pdfFonts.pdfMake) {
  pdfMake.vfs = pdfFonts.pdfMake.vfs;
}

const MONTH_NAMES = [
  'Ocak',
  'Şubat',
  'Mart',
  'Nisan',
  'Mayıs',
  'Haziran',
  'Temmuz',
  'Ağustos',
  'Eylül',
  'Ekim',
  'Kasım',
  'Aralık',
];
function InteractivePieChart({ data, size = 200 }) {
  const [hoverData, setHoverData] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const total = data.reduce((acc, item) => acc + item.value, 0);
  let currentAngle = 0;
  const segments = data.map((item) => {
    const degree = (item.value / total) * 360;
    const start = currentAngle;
    const end = currentAngle + degree;
    currentAngle += degree;

    return { ...item, startAngle: start, endAngle: end, degree };
  });

  const gradientParts = segments.map((s) => {
    return `${s.color} ${s.startAngle}deg ${s.endAngle}deg`;
  });
  const gradientString =
    total > 0 ? `conic-gradient(${gradientParts.join(', ')})` : '#e0e0e0';

  const handleMouseMove = (e) => {
    if (total === 0) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const x = e.clientX - rect.left - centerX;
    const y = e.clientY - rect.top - centerY;
    let angle = Math.atan2(y, x) * (180 / Math.PI) + 90;
    if (angle < 0) angle += 360;
    const found = segments.find(
      (s) => angle >= s.startAngle && angle < s.endAngle
    );

    if (found) {
      setHoverData(found);
      setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    } else {
      setHoverData(null);
    }
  };

  const handleMouseLeave = () => {
    setHoverData(null);
  };

  return (
    <Box sx={{ position: 'relative', width: size, margin: '0 auto' }}>
      <Box
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: gradientString,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          cursor: 'pointer',
          touchAction: 'none',
        }}
      />
      {hoverData && (
        <Box
          sx={{
            position: 'absolute',
            top: mousePos.y - 40,
            left: mousePos.x,
            transform: 'translateX(-50%)',
            bgcolor: 'rgba(0, 0, 0, 0.8)',
            color: '#fff',
            padding: '6px 12px',
            borderRadius: 1,
            pointerEvents: 'none',
            zIndex: 10,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          }}
        >
          <Typography variant="caption" fontWeight="bold" display="block">
            {hoverData.name}
          </Typography>
          <Typography variant="caption">
            {hoverData.value} Adet (%
            {((hoverData.value / total) * 100).toFixed(1)})
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function CustomDonutChart({ data, size = 200, strokeWidth = 15 }) {
  const center = size / 2;
  const radius = (size - strokeWidth) / 2;
  const total = data.reduce((acc, item) => acc + item.value, 0);

  if (total === 0) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="#eee"
          strokeWidth={strokeWidth}
        />
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dy=".3em"
          fill="#999"
          fontSize="12"
        >
          Veri Yok
        </text>
      </svg>
    );
  }

  let currentAngle = 0;
  const getCoordinates = (angleInDegrees) => {
    const angleInRadians = (angleInDegrees - 90) * (Math.PI / 180.0);
    return {
      x: center + radius * Math.cos(angleInRadians),
      y: center + radius * Math.sin(angleInRadians),
    };
  };

  const slices = data.map((item, index) => {
    const sliceAngle = (item.value / total) * 360;
    const start = getCoordinates(currentAngle);
    const end = getCoordinates(currentAngle + sliceAngle);
    const largeArcFlag = sliceAngle > 180 ? 1 : 0;

    const pathData =
      total === item.value
        ? `M ${center}, ${
            center - radius
          } A ${radius},${radius} 0 1,1 ${center},${
            center + radius
          } A ${radius},${radius} 0 1,1 ${center},${center - radius}`
        : `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`;

    const slice = (
      <path
        key={index}
        d={pathData}
        fill="none"
        stroke={item.color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    );
    currentAngle += sliceAngle;
    return slice;
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {slices}
    </svg>
  );
}

function CustomComboChart({ title, data, barColor, lineColor }) {
  const [hoverData, setHoverData] = useState(null);

  const width = 600; 
  const height = 350;
  const padding = 50;
  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;
  const maxCount = Math.max(...data.map((d) => d.count), 5) * 1.1; 
  const maxPrice = Math.max(...data.map((d) => d.price), 50) * 1.1;
  const barWidth = (chartWidth / data.length) * 0.6;
  const getX = (index) =>
    padding + index * (chartWidth / data.length) + chartWidth / data.length / 2;
  const getYCount = (val) =>
    padding + chartHeight - (val / maxCount) * chartHeight;
  const getYPrice = (val) =>
    padding + chartHeight - (val / maxPrice) * chartHeight;

  let linePath = '';
  data.forEach((d, i) => {
    const x = getX(i);
    const y = getYPrice(d.price);
    if (i === 0) linePath += `M ${x} ${y}`;
    else linePath += ` L ${x} ${y}`;
  });

  return (
    <Card
      sx={{
        height: '100%',
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        borderRadius: 3,
        position: 'relative',
      }}
    >
      <CardContent>
        <Typography
          variant="subtitle2"
          align="center"
          fontWeight="bold"
          mb={3}
          color="#444"
        >
          {title}
        </Typography>

        <Box
          sx={{
            width: '100%',
            overflowX: 'auto',
            display: 'flex',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
            {[0, 0.2, 0.4, 0.6, 0.8, 1].map((t, i) => (
              <line
                key={i}
                x1={padding}
                y1={padding + chartHeight * t}
                x2={width - padding}
                y2={padding + chartHeight * t}
                stroke="#f0f0f0"
                strokeWidth="1"
              />
            ))}
            {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
              const val = Math.round(maxCount * (1 - t));
              return (
                <text
                  key={`left-${i}`}
                  x={padding - 10}
                  y={padding + chartHeight * t + 4}
                  textAnchor="end"
                  fontSize="10"
                  fill="#999"
                >
                  {val}
                </text>
              );
            })}
            {data.map((d, i) => {
              const x = getX(i) - barWidth / 2;
              const y = getYCount(d.count);
              const h = chartHeight - (y - padding);
              return (
                <g
                  key={`bar-${i}`}
                  onMouseEnter={() =>
                    setHoverData({ ...d, type: 'bar', x: getX(i), y: y })
                  }
                  onMouseLeave={() => setHoverData(null)}
                >
                  <rect
                    x={x}
                    y={y}
                    width={barWidth}
                    height={h}
                    fill={barColor}
                    opacity={0.8}
                    rx={2}
                    style={{ transition: 'all 0.2s' }}
                  />
                  <text
                    x={x + barWidth / 2}
                    y={y - 5}
                    textAnchor="middle"
                    fontSize="10"
                    fontWeight="bold"
                    fill="#666"
                  >
                    {d.count > 0 ? d.count : ''}
                  </text>
                  <text
                    x={x + barWidth / 2}
                    y={height - padding + 15}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#666"
                  >
                    {d.name.substring(0, 3)}
                  </text>
                </g>
              );
            })}
            <path
              d={linePath}
              fill="none"
              stroke={lineColor}
              strokeWidth={3}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {data.map((d, i) => (
              <g
                key={`dot-${i}`}
                onMouseEnter={() =>
                  setHoverData({
                    ...d,
                    type: 'line',
                    x: getX(i),
                    y: getYPrice(d.price),
                  })
                }
                onMouseLeave={() => setHoverData(null)}
                style={{ cursor: 'pointer' }}
              >
                <circle
                  cx={getX(i)}
                  cy={getYPrice(d.price)}
                  r={5}
                  fill="#fff"
                  stroke={lineColor}
                  strokeWidth={2}
                />
              </g>
            ))}
          </svg>
          {hoverData && (
            <div
              style={{
                position: 'absolute',
                left: hoverData.x,
                top: hoverData.y - 50,
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0,0,0,0.8)',
                color: 'white',
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                pointerEvents: 'none',
                zIndex: 10,
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
              }}
            >
              <strong>{hoverData.name}</strong>
              <br />
              {hoverData.type === 'bar' ? (
                <>İşlem: {hoverData.count} Adet</>
              ) : (
                <>
                  Tutar:{' '}
                  {hoverData.price.toLocaleString('tr-TR', {
                    minimumFractionDigits: 2,
                  })}{' '}
                  ₺
                </>
              )}
            </div>
          )}
        </Box>
        <Stack direction="row" justifyContent="center" gap={3} mt={1}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box width={12} height={12} bgcolor={barColor} borderRadius={1} />
            <Typography variant="caption" color="textSecondary">
              İşlem Adedi
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box width={12} height={4} bgcolor={lineColor} borderRadius={1} />
            <Typography variant="caption" color="textSecondary">
              Toplam Tutar (TL)
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

const InfoCard = ({
  title,
  totalValue,
  subText,
  data,
  avmList,
  selectedAvm,
  onAvmChange,
}) => {
  const isPrice = title.includes('Tutar');

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 2,
        boxShadow: '0px 0px 10px rgba(0,0,0,0.05)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            color="#333"
            sx={{ maxWidth: '60%' }}
          >
            {title}
          </Typography>
          <FormControl sx={{ width: '100%', maxWidth: '200px' }}>
            <Select
              value={selectedAvm}
              onChange={onAvmChange}
              displayEmpty
              sx={{
                width: '100%',
                height: 44,
                fontSize: 13,
                borderRadius: 1,
                bgcolor: '#fff',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#eee' },
              }}
            >
              <MenuItem value="tumu">Tüm AVM'ler</MenuItem>
              {avmList.map((mall) => (
                <MenuItem key={mall.title} value={mall.title}>
                  {mall.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          position="relative"
          minHeight={220}
        >
          <CustomDonutChart data={data} size={220} strokeWidth={18} />
          <Box
            position="absolute"
            top={0}
            left={0}
            bottom={0}
            right={0}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <Typography variant="h5" fontWeight="bold" color="#333">
              {totalValue}
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ fontSize: 11 }}
            >
              {subText}
            </Typography>
          </Box>
        </Box>

        <Stack
          direction="row"
          justifyContent="center"
          mt={3}
          gap={1}
          pt={1.5}
          flexWrap="wrap"
          borderTop="1px solid rgba(229, 231, 235, 1)"
        >
          {data.map((item, idx) => (
            <Box
              key={idx}
              display="flex"
              alignItems="center"
              sx={{ opacity: 0.9, gap: 0.5 }}
            >
              <Box
                width={6}
                height={6}
                borderRadius="50%"
                bgcolor={item.color}
              />
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ fontSize: 13 }}
              >
                {item.name}:{' '}
                <Box component="span" fontWeight="bold" color="#333">
                  {item.value.toLocaleString('tr-TR', {
                    minimumFractionDigits: isPrice ? 2 : 0,
                    maximumFractionDigits: isPrice ? 2 : 0,
                  })}
                  {isPrice ? ' ₺' : ''}
                </Box>
              </Typography>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default function EasyPointServicesReport() {
  const [loading, setLoading] = useState(false);
  const [avmList, setAvmList] = useState([]);
  const [selectedAvm, setSelectedAvm] = useState('tumu');

  // Year Data
  const [monthlyData, setMonthlyData] = useState([]);
  const [donutDataPrice, setDonutDataPrice] = useState([]);
  const [donutDataCount, setDonutDataCount] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [chartDataHandsfree, setChartDataHandsfree] = useState([]);
  const [chartDataKid, setChartDataKid] = useState([]);
  const [chartDataPet, setChartDataPet] = useState([]);
  const [transactionData, setTransactionData] = useState([]);
  const [filterConfig, setFilterConfig] = useState({});
  const [showFilterInput, setShowFilterInput] = useState({});

  const user = JSON.parse(localStorage.getItem('user'));
  const token = user?.token;

  const parsePrice = (priceInfo) => {
    if (!priceInfo) return 0;
    if (typeof priceInfo === 'object' && priceInfo.price)
      return parseFloat(priceInfo.price);
    const str = String(priceInfo);
    const cleanStr = str.replace(/[^0-9.,]/g, '').replace(',', '.');
    return parseFloat(cleanStr) || 0;
  };

  const mapServiceType = (type) => {
    switch (type) {
      case 'gift':
        return 'Hediye Paketi';
      case 'pet-car':
        return 'Evcil Hayvan Aracı';
      case 'kid-car':
        return 'Çocuk Arabası';
      default:
        return 'Handsfree';
    }
  };

  const mapServiceColor = (name) => {
    switch (name) {
      case 'Evcil Hayvan Aracı':
        return '#EB5757';
      case 'Çocuk Arabası':
        return '#F2994A';
      case 'Hediye Paketi':
        return '#27AE60';
      default:
        return '#F2C94C';
    }
  };

  const getCityFromMall = (mallTitle) => {
    const mall = avmList.find((m) => m.title === mallTitle);
    return mall?.city || mall?.province || 'Belirsiz';
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const now = new Date();
      const currentMonth = now.getMonth();
      const daysInCurrentMonthPassed = now.getDate();

      const startOfYear = new Date(now.getFullYear(), 0, 1);
      const today = new Date();

      const fromDate = startOfYear.toISOString().split('T')[0];
      const toDate = today.toISOString().split('T')[0];

      const res = await Services.GetEPServices(token, fromDate, toDate);
      let rawData = res.data.result || [];

      rawData = rawData.filter((item) => item.isPaid !== '0');

      if (selectedAvm !== 'tumu') {
        rawData = rawData.filter((item) => item.mallTitle === selectedAvm);
      }

      // 1. CHART DATA PREPARATION
      const initMonths = () =>
        Array.from({ length: 12 }, (_, i) => ({
          name: MONTH_NAMES[i],
          count: 0,
          price: 0,
        }));
      const handsfreeStats = initMonths();
      const kidStats = initMonths();
      const petStats = initMonths();

      rawData.forEach((item) => {
        const date = new Date(item.createdDateTime * 1000);
        const mIdx = date.getMonth();
        const type = mapServiceType(item.type);
        const price =
          parsePrice(item.deliveryTypeInfo?.price || item.price || 0) / 1.2;

        if (type === 'Handsfree') {
          handsfreeStats[mIdx].count += 1;
          handsfreeStats[mIdx].price += price;
        } else if (type === 'Çocuk Arabası') {
          kidStats[mIdx].count += 1;
          kidStats[mIdx].price += price;
        } else if (type === 'Evcil Hayvan Aracı') {
          petStats[mIdx].count += 1;
          petStats[mIdx].price += price;
        }
      });

      setChartDataHandsfree(handsfreeStats);
      setChartDataKid(kidStats);
      setChartDataPet(petStats);

      // 2. SUMMARY DATA
      const monthlyStats = MONTH_NAMES.map((m) => ({
        month: m,
        count: 0,
        price: 0,
      }));
      rawData.forEach((item) => {
        const date = new Date(item.createdDateTime * 1000);
        const mIndex = date.getMonth();
        const pVal =
          parsePrice(item.deliveryTypeInfo?.price || item.price) / 1.2;
        monthlyStats[mIndex].count += 1;
        monthlyStats[mIndex].price += pVal;
      });

      const finalMonthlyData = monthlyStats
        .slice(0, currentMonth + 1)
        .map((m) => ({
          ...m,
          price:
            m.price.toLocaleString('tr-TR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) + ' ₺',
        }));

      const categoryStats = {};
      rawData.forEach((item) => {
        const sName = mapServiceType(item.type);
        const pVal =
          parsePrice(item.deliveryTypeInfo?.price || item.price) / 1.2;
        if (!categoryStats[sName])
          categoryStats[sName] = { count: 0, price: 0 };
        categoryStats[sName].count += 1;
        categoryStats[sName].price += pVal;
      });

      const dDataPrice = Object.keys(categoryStats).map((key) => ({
        name: key,
        value: categoryStats[key].price,
        color: mapServiceColor(key),
      }));
      const dDataCount = Object.keys(categoryStats).map((key) => ({
        name: key,
        value: categoryStats[key].count,
        color: mapServiceColor(key),
      }));

      setMonthlyData(finalMonthlyData);
      setDonutDataPrice(dDataPrice);
      setDonutDataCount(dDataCount);
      setTotalPrice(dDataPrice.reduce((acc, i) => acc + i.value, 0));
      setTotalCount(dDataCount.reduce((acc, i) => acc + i.value, 0));
      const currentMonthRawData = rawData.filter((item) => {
        const d = new Date(item.createdDateTime * 1000);
        return (
          d.getMonth() === currentMonth && d.getFullYear() === now.getFullYear()
        );
      });

      const groupedMap = {};
      currentMonthRawData.forEach((item) => {
        const serviceName = mapServiceType(item.type);
        const mallName = item.mallTitle;
        const priceVal =
          parsePrice(item.deliveryTypeInfo?.price || item.price || 0) / 1.2;

        const key = `${mallName}_${serviceName}`;
        if (!groupedMap[key]) {
          groupedMap[key] = {
            avm: mallName,
            service: serviceName,
            count: 0,
            totalPrice: 0,
            city: getCityFromMall(mallName),
          };
        }
        groupedMap[key].count += 1;
        groupedMap[key].totalPrice += priceVal;
      });

      const processedTransactions = Object.values(groupedMap).map(
        (item, index) => {
          const avgDailyCount = item.count / daysInCurrentMonthPassed;
          const avgDailyRevenue = item.totalPrice / daysInCurrentMonthPassed;
          const revPerTx = item.count > 0 ? item.totalPrice / item.count : 0;

          return {
            no: index + 1,
            city: item.city,
            avm: item.avm,
            service: item.service,
            pointCount: '-',
            count: item.count,
            dailyAvgCount: avgDailyCount.toFixed(2),
            revPerTx:
              revPerTx.toLocaleString('tr-TR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }) + ' ₺',
            dailyAvgRev:
              avgDailyRevenue.toLocaleString('tr-TR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }) + ' ₺',
            total:
              item.totalPrice.toLocaleString('tr-TR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }) + ' ₺',
            rawTotal: item.totalPrice,
          };
        }
      );

      processedTransactions.sort((a, b) => a.avm.localeCompare(b.avm));
      setTransactionData(processedTransactions);
    } catch (error) {
      console.error('Veri çekme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMalls = async () => {
    try {
      const res = await Services.GetMalls(token);
      setAvmList(res.data.result || []);
    } catch (err) {
      console.error('AVM Listesi Hatası', err);
    }
  };

  useEffect(() => {
    fetchMalls();
  }, []);

  useEffect(() => {
    if (avmList.length > 0) fetchData();
  }, [selectedAvm, avmList]);
  const toggleFilterInput = (key) => {
    setShowFilterInput((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const handleFilterChange = (key, value) => {
    setFilterConfig((prev) => ({ ...prev, [key]: value }));
  };
  const clearColumnFilter = (key) => {
    setFilterConfig((prev) => {
      const newF = { ...prev };
      delete newF[key];
      return newF;
    });
    setShowFilterInput((prev) => ({ ...prev, [key]: false }));
  };

  const filteredTransactionData = useMemo(() => {
    let items = [...transactionData];
    Object.keys(filterConfig).forEach((key) => {
      const value = filterConfig[key];
      if (value) {
        items = items.filter((item) =>
          item[key]
            ?.toString()
            .toLocaleLowerCase('tr-TR')
            .includes(value.toLocaleLowerCase('tr-TR'))
        );
      }
    });
    return items;
  }, [transactionData, filterConfig]);
  const handleExportPDF = () => {
    const tableBody = [
      [
        { text: 'No', bold: true, fillColor: '#EB5757', color: 'white' },
        { text: 'Şehir', bold: true, fillColor: '#EB5757', color: 'white' },
        { text: 'AVM', bold: true, fillColor: '#EB5757', color: 'white' },
        {
          text: 'Hizmet Türü',
          bold: true,
          fillColor: '#EB5757',
          color: 'white',
        },
        {
          text: 'İşlem Adedi',
          bold: true,
          fillColor: '#EB5757',
          color: 'white',
        },
        {
          text: 'Günlük Ort.',
          bold: true,
          fillColor: '#EB5757',
          color: 'white',
        },
        {
          text: 'Ciro / İşlem',
          bold: true,
          fillColor: '#EB5757',
          color: 'white',
        },
        {
          text: 'Toplam Tutar',
          bold: true,
          fillColor: '#EB5757',
          color: 'white',
        },
      ],
      ...filteredTransactionData.map((row, idx) => [
        idx + 1,
        row.city,
        row.avm,
        row.service,
        row.count,
        row.dailyAvgCount,
        row.revPerTx,
        row.total,
      ]),
    ];
    const docDefinition = {
      content: [
        { text: 'Hizmet İşlemi Raporu', style: 'header' },
        {
          table: {
            headerRows: 1,
            widths: ['auto', 'auto', '*', '*', 'auto', 'auto', 'auto', 'auto'],
            body: tableBody,
          },
        },
      ],
      styles: { header: { fontSize: 16, bold: true, margin: [0, 0, 0, 10] } },
    };
    pdfMake.createPdf(docDefinition).download('hizmet_raporu.pdf');
  };

  const handleExportExcel = () => {
    const content = filteredTransactionData.map((item, idx) => ({
      No: idx + 1,
      Sehir: item.city,
      AVM: item.avm,
      HizmetTuru: item.service,
      IslemAdedi: item.count,
      GunlukOrt: item.dailyAvgCount,
      CiroIslem: item.revPerTx,
      ToplamTutar: item.total,
    }));
    api
      .post('excel/export', {
        type: 'excel',
        content: content,
        header: {
          No: 'number',
          Sehir: 'string',
          AVM: 'string',
          HizmetTuru: 'string',
          IslemAdedi: 'number',
          GunlukOrt: 'string',
          CiroIslem: 'string',
          ToplamTutar: 'string',
        },
        sheetTitle: 'Hizmet İşlemi Raporu',
      })
      .then((response) => {
        if (response.data.status) window.open(response.data.result, '_blank');
      })
      .catch((err) => console.error('Excel export hatası:', err));
  };

  const renderHeaderCell = (label, key) => {
    const isFilterable = ['avm', 'service', 'city'].includes(key);
    if (!isFilterable) {
      return (
        <TableCell
          sx={{
            backgroundColor: '#EB5757',
            color: '#fff',
            fontWeight: '700',
            fontSize: 13,
          }}
        >
          {label}
        </TableCell>
      );
    }
    return (
      <TableCell
        sx={{
          backgroundColor: '#EB5757',
          color: '#fff',
          fontWeight: '700',
          fontSize: 13,
          minWidth: 120,
          py: 1,
        }}
      >
        {showFilterInput[key] ? (
          <Box display="flex" alignItems="center" gap={1}>
            <TextField
              variant="standard"
              placeholder={`${label} Ara`}
              value={filterConfig[key] || ''}
              onChange={(e) => handleFilterChange(key, e.target.value)}
              autoFocus
              InputProps={{
                disableUnderline: true,
                style: { fontSize: 13, padding: '2px 8px' },
              }}
              sx={{ bgcolor: 'white', borderRadius: 1, width: '100%' }}
            />
            <IconButton
              size="small"
              onClick={() => clearColumnFilter(key)}
              sx={{ color: 'white', padding: 0 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            onClick={() => toggleFilterInput(key)}
            sx={{ cursor: 'pointer' }}
          >
            {label} <SearchIcon fontSize="small" sx={{ opacity: 0.7 }} />
          </Box>
        )}
      </TableCell>
    );
  };

  return (
    <Box p={3} minHeight="100vh">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h6" fontWeight="bold" color="#111">
          Easy Point Hizmetleri Raporu
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ fontSize: 11 }}
        >
          *Fiyatlara KDV Dahil Değildir
        </Typography>
      </Box>

      {/* ÜST BİLGİ KARTLARI */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={6}>
          <InfoCard
            title="Avm Bazlı Toplam Tutar"
            totalValue={`${totalPrice.toLocaleString('tr-TR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} ₺`}
            subText="Toplam Tutar"
            data={donutDataPrice}
            avmList={avmList}
            selectedAvm={selectedAvm}
            onAvmChange={(e) => setSelectedAvm(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InfoCard
            title="AVM Bazlı Toplam Adet"
            totalValue={totalCount.toLocaleString()}
            subText="Toplam Miktar"
            data={donutDataCount}
            avmList={avmList}
            selectedAvm={selectedAvm}
            onAvmChange={(e) => setSelectedAvm(e.target.value)}
          />
        </Grid>
      </Grid>

      {/* ÜST AYLIK TABLO */}
      <Grid container justifyContent="center" mb={4}>
        <Grid item xs={12} md={6}>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: '0px 0px 10px rgba(0,0,0,0.05)',
            }}
          >
            <Box
              bgcolor="#EB5757"
              p={2}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={1}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <img
                  src="/assets/ep_circle_logo_white.png"
                  alt="logo"
                  style={{ height: 40 }}
                />
                <Typography variant="subtitle1" color="#fff" fontWeight="bold">
                  Easy Point Hizmetleri Toplam <br /> Kiralama Verileri (
                  {new Date().getFullYear()})
                </Typography>
              </Box>
            </Box>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#EB5757' }}>
                  <TableCell
                    sx={{
                      backgroundColor: '#EB5757',
                      color: '#fff',
                      fontWeight: '700',
                      fontSize: 13,
                      py: 1.5,
                    }}
                  >
                    Aylar
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor: '#EB5757',
                      color: '#fff',
                      fontWeight: '700',
                      fontSize: 13,
                      py: 1.5,
                    }}
                  >
                    Toplam Kiralama İşlemi
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: '#EB5757',
                      color: '#fff',
                      fontWeight: '700',
                      fontSize: 13,
                      py: 1.5,
                    }}
                  >
                    Toplam Fiyat
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {monthlyData.length > 0 ? (
                  monthlyData.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        '&:nth-of-type(odd)': { bgcolor: '#fff' },
                        '&:nth-of-type(even)': { bgcolor: '#fafafa' },
                      }}
                    >
                      <TableCell
                        sx={{ fontWeight: '700', fontSize: 13, color: '#333' }}
                      >
                        {row.month}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: 13, color: '#555' }}
                      >
                        {row.count}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontSize: 13, color: '#555' }}
                      >
                        {row.price}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      Veri Yok
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
      <Paper
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0px 0px 10px rgba(0,0,0,0.05)',
          mb: 5,
        }}
      >
        <Box
          bgcolor="#EB5757"
          p={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="subtitle1" color="#fff" fontWeight="bold">
            Hizmet İşlemi Adetleri ({MONTH_NAMES[new Date().getMonth()]}){' '}
            {loading && (
              <CircularProgress size={20} sx={{ color: 'white', ml: 2 }} />
            )}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              onClick={handleExportPDF}
              endIcon={<DownloadIcon sx={{ width: 16 }} />}
              sx={{
                bgcolor: '#fff',
                color: 'rgba(96, 101, 110, 1)',
                fontSize: 13,
                fontWeight: '700',
                textTransform: 'none',
                py: 1,
                '&:hover': { bgcolor: '#f0f0f0' },
              }}
              size="small"
            >
              Pdf olarak indir
            </Button>
            <Button
              variant="contained"
              onClick={handleExportExcel}
              endIcon={<DownloadIcon sx={{ width: 16 }} />}
              sx={{
                bgcolor: '#fff',
                color: 'rgba(96, 101, 110, 1)',
                fontSize: 13,
                fontWeight: '700',
                textTransform: 'none',
                py: 1,
                '&:hover': { bgcolor: '#f0f0f0' },
              }}
              size="small"
            >
              Excel'e aktar
            </Button>
          </Stack>
        </Box>

        <TableContainer sx={{ maxHeight: 400 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: '#EB5757',
                    color: '#fff',
                    fontWeight: '700',
                    fontSize: 13,
                  }}
                >
                  No
                </TableCell>
                {renderHeaderCell('Şehir', 'city')}
                {renderHeaderCell('AVM', 'avm')}
                {renderHeaderCell('Hizmet Türü', 'service')}
                <TableCell
                  sx={{
                    backgroundColor: '#EB5757',
                    color: '#fff',
                    fontWeight: '700',
                    fontSize: 13,
                  }}
                >
                  Nokta Araç Sayısı
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#EB5757',
                    color: '#fff',
                    fontWeight: '700',
                    fontSize: 13,
                  }}
                >
                  Toplam İşlem Adedi
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#EB5757',
                    color: '#fff',
                    fontWeight: '700',
                    fontSize: 13,
                  }}
                >
                  Günlük Ort. Kiralama
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#EB5757',
                    color: '#fff',
                    fontWeight: '700',
                    fontSize: 13,
                  }}
                >
                  Ciro / İşlem
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#EB5757',
                    color: '#fff',
                    fontWeight: '700',
                    fontSize: 13,
                  }}
                >
                  Günlük Ort. Ciro
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#EB5757',
                    color: '#fff',
                    fontWeight: '700',
                    fontSize: 13,
                  }}
                >
                  Toplam Ciro
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTransactionData.map((row, idx) => (
                <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                  <TableCell sx={{ fontWeight: '700', fontSize: 13 }}>
                    {idx + 1}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{row.city}</TableCell>
                  <TableCell sx={{ color: '#555', fontSize: 13 }}>
                    {row.avm}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{row.service}</TableCell>
                  <TableCell sx={{ fontSize: 13, textAlign: 'center' }}>
                    {row.pointCount}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{row.count} Adet</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>
                    {row.dailyAvgCount}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{row.revPerTx}</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{row.dailyAvgRev}</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{row.total}</TableCell>
                </TableRow>
              ))}
              {filteredTransactionData.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    Kayıt Bulunamadı
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Typography variant="h6" fontWeight="bold" color="#333" mb={2} mt={4} textAlign="center">
        Hizmet Bazlı Detay Grafikleri (Yıllık)
      </Typography>
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={6}>
          <CustomComboChart
            title="Handsfree Kiralama İşlemleri (2026)"
            data={chartDataHandsfree}
            barColor="#F2994A"
            lineColor="#D97706"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomComboChart
            title="Çocuk Arabası Kiralama İşlemleri (2026)"
            data={chartDataKid}
            barColor="#F2994A"
            lineColor="#D97706"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomComboChart
            title="Evcil Hayvan Arabası Kiralama İşlemleri (2026)"
            data={chartDataPet}
            barColor="#27AE60"
            lineColor="#15803D"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              height: '100%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              borderRadius: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 3,
            }}
          >
            <Typography
              variant="subtitle2"
              align="center"
              fontWeight="bold"
              mb={2}
              color="#444"
            >
              Toplam Kiralama Hizmet Dağılımı
            </Typography>
            <InteractivePieChart data={donutDataCount} size={240} />
            <Stack
              direction="row"
              gap={2}
              mt={3}
              flexWrap="wrap"
              justifyContent="center"
            >
              {donutDataCount.map((d, i) => {
                const total = donutDataCount.reduce(
                  (acc, item) => acc + item.value,
                  0
                );
                const percent =
                  total > 0 ? ((d.value / total) * 100).toFixed(1) : 0;

                return (
                  <Box key={i} display="flex" alignItems="center" gap={0.8}>
                    <Box
                      width={10}
                      height={10}
                      borderRadius="50%"
                      bgcolor={d.color}
                    />
                    <Typography variant="caption" color="textSecondary">
                      {d.name}
                      <span
                        style={{
                          fontWeight: 'bold',
                          color: '#333',
                          marginLeft: '4px',
                        }}
                      >
                        (%{percent})
                      </span>
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
